var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FieldWrapper, FormField, FormGrid, Page, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { numbersApi } from 'apis/numbers';
import { HookFormPhoneNumberSelect } from 'components/hookForm';
import { actions as personalActions } from 'components/settings/actions/personal';
import { ToggleBox, ToggleBoxHead } from 'fable/toggleBox';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import styles from './profileFormSettings.module.scss';
export const ProfileFormSettings = () => {
    const dispatch = useAppDispatch();
    const { control, setValue } = useFormContext();
    const { newKeybindings } = useFeatureFlag(['new-keybindings']);
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const personalState = useAppSelector((state) => state.settings.personal);
    const userDefaultOutboundNumber = useAppSelector((state) => state.settings.profile.profile.default_outbound_number);
    const organizationDefaultOutboundNumber = useAppSelector((state) => state.settings.organization.organization.default_outbound_number);
    const { defaultOutboundNumber } = numbersApi.useReadDefaultNumberQuery(undefined, {
        skip: !userDefaultOutboundNumber,
        selectFromResult: (_a) => {
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { defaultOutboundNumber: data === null || data === void 0 ? void 0 : data.data }));
        },
    });
    // Sets the initial value of the default outbound number field to the user's preferred default. We
    // do this check because in case the user doesn't have a preferred default, the numbers API will
    // return the organization default if it exists, and we do not want to use that as the initial
    // value in the form, to prevent the organization default from being set as the user default.
    useEffect(() => {
        if (defaultOutboundNumber &&
            defaultOutboundNumber.e164_format === userDefaultOutboundNumber) {
            setValue('default_outbound_number', [defaultOutboundNumber]);
        }
    }, [defaultOutboundNumber, setValue, userDefaultOutboundNumber]);
    const handleChangeKeyboardShortcuts = useCallback((value) => {
        dispatch(personalActions.personalKeyboardShortcuts({
            key: 'keyboardShortcuts',
            value,
        }));
    }, [dispatch]);
    return (_jsx(Page.Section, Object.assign({ title: "Settings" }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 3 }, { children: _jsx(HookFormPhoneNumberSelect, { name: "default_outbound_number", label: "Default outbound number", control: control, rules: {
                            validate: {
                                isOutbound: (phoneNumber) => (phoneNumber === null || phoneNumber === void 0 ? void 0 : phoneNumber.length)
                                    ? phoneNumber[0].outgoing_functionality ||
                                        'The selected phone number does not support outbound calls'
                                    : true,
                            },
                        }, componentProps: {
                            showCountryFlag: true,
                            clearable: true,
                        }, helpIcon: !userDefaultOutboundNumber && organizationDefaultOutboundNumber
                            ? {
                                text: `If you don't select your preferred default outbound number, the organization's default outbound number will be used: ${organizationDefaultOutboundNumber}`,
                                dark: true,
                                tooltipClassName: styles.tooltip,
                            }
                            : undefined }) })), !newKeybindings && (_jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 3 }, { children: _jsx(FieldWrapper, Object.assign({ label: "Keyboard shortcuts" }, { children: _jsx(ToggleBox, Object.assign({ title: `Keyboard shortcuts ${personalState.keyboardShortcuts ? 'enabled' : 'disabled'}`, isOpen: personalState.keyboardShortcuts, headComponent: ToggleBoxHead, textProps: { variant: '3' }, input: {
                                name: 'keyboard_shortcuts',
                                value: 'keyboard_shortcuts',
                            }, onChange: handleChangeKeyboardShortcuts, disabled: personalState.meta.asyncStatus === 'loading' }, { children: _jsx(Box, Object.assign({ padding: "0 300 300" }, { children: _jsx(Text.ExternalLink, Object.assign({ href: "#", "data-elevio-article": articles.settings.profile.keyboardShortcuts, "data-elevio-style": "nothing" }, { children: "See all the keyboard shortcuts" })) })) })) })) })))] }) })));
};
